import { Button, Divider, Input, Typography } from "@mui/material";
import { capitalize } from "common/utils/strings";
import { DateTime } from "luxon";
import {
  endPointMove,
  setPointMoveCarrying,
  startPointMove,
} from "portal/state/farmExplorer";
import { FixType, Point as PointProto } from "protos/geo/geo";
import { NumberInput } from "common/components/NumberInput";
import { PointDefinition } from "protos/farm/farm";
import { useCanEditFarmGeometry } from "portal/views/farms/permissions";
import { useDispatch } from "react-redux";
import { useFarmExplorer } from "portal/state/store";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import PlaceIcon from "@mui/icons-material/Place";
import React, { FC, useEffect, useMemo } from "react";

interface EditablePointProps {
  point?: PointProto;
  disabled?: boolean;
  onCancel?: () => void;
  onSave?: (p: PointDefinition) => void;
}

export interface FixInfo {
  color: string;
  text: string;
}

export const EditablePoint: FC<EditablePointProps> = ({
  point,
  disabled,
  onCancel,
  onSave,
}: EditablePointProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { farm, pointMove } = useFarmExplorer();
  const canEditGeometry = useCanEditFarmGeometry();
  const pointId = point?.id?.id;
  const isMoving = Boolean(pointId && pointMove?.pointId === pointId);
  const isCarrying = isMoving && Boolean(pointMove?.carrying);
  const pointDef = useMemo(() => {
    return farm?.pointDefs.find((p) => p.point?.id?.id === point?.id?.id);
  }, [farm, point]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      lat: pointDef?.point?.lat ?? 0,
      lng: pointDef?.point?.lng ?? 0,
      name: pointDef?.point?.name ?? "",
      ordinal: pointDef?.version?.ordinal ?? 1,
    },
    onSubmit: (values) => {
      if (!point) {
        return;
      }
      dispatch(endPointMove());
      onSave?.({
        point: {
          ...point,
          lat: values.lat,
          lng: values.lng,
          name: values.name,
          captureInfo: {
            ...point.captureInfo,
            fixType: FixType.NO_FIX,
            captureTime: DateTime.now().toISO(),
          },
        },
        version: {
          ordinal: values.ordinal,
          updateTime: DateTime.now().toISO(),
          changed: true,
          deleted: false,
        },
      });
    },
  });

  // the form follows the point while it is moved on the map
  const { setFieldValue } = formik;
  const movedLat = isMoving ? pointMove?.lat : undefined;
  const movedLng = isMoving ? pointMove?.lng : undefined;
  useEffect(() => {
    if (movedLat !== undefined && movedLng !== undefined) {
      setFieldValue("lat", movedLat);
      setFieldValue("lng", movedLng);
    }
  }, [movedLat, movedLng, setFieldValue]);

  // so map clicks stop moving a point the user can no longer see
  useEffect(
    () => () => {
      dispatch(endPointMove());
    },
    [dispatch]
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="px-2 flex flex-col gap-2">
        <div className="flex flex-col">
          <Typography className="text-sm">
            {capitalize(t("utils.descriptors.id"))}
          </Typography>
          <Typography className="font-bold">{point?.id?.id}</Typography>
        </div>
        <Divider />
        <div className="flex flex-col">
          <Typography className="text-sm">
            {capitalize(t("utils.descriptors.name"))}
          </Typography>
          <Input
            title={t("utils.descriptors.name")}
            value={formik.values.name}
            onChange={(e) => formik.setFieldValue("name", e.target.value)}
          />
        </div>
        <Divider />
        <div className="flex flex-col gap-2">
          <Typography className="text-sm">
            {capitalize(t("models.farms.coordinate", { count: 2 }))}
          </Typography>
          <NumberInput
            label={capitalize(t("models.farms.longitude"))}
            value={formik.values.lng}
            onChange={(e) => formik.setFieldValue("lng", e ?? 0)}
          />
          <NumberInput
            label={capitalize(t("models.farms.latitude"))}
            value={formik.values.lat}
            onChange={(e) => formik.setFieldValue("lat", e ?? 0)}
          />
          {canEditGeometry && pointId && (
            <Button
              variant={isCarrying ? "contained" : "outlined"}
              startIcon={<PlaceIcon />}
              disabled={disabled}
              onClick={() => {
                if (isCarrying) {
                  dispatch(endPointMove());
                } else if (isMoving) {
                  dispatch(setPointMoveCarrying(true));
                } else {
                  dispatch(
                    startPointMove({
                      pointId,
                      lat: formik.values.lat,
                      lng: formik.values.lng,
                    })
                  );
                }
              }}
            >
              {isCarrying
                ? t("views.farms.editBoundary.cancelMove")
                : t("views.farms.editBoundary.moveOnMap")}
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={!formik.dirty || disabled}
          >
            {t("utils.actions.save")}
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="inherit"
            disabled={!formik.dirty || disabled}
            onClick={() => {
              dispatch(endPointMove());
              formik.resetForm();
              onCancel?.();
            }}
          >
            {t("utils.actions.cancel")}
          </Button>
        </div>
      </div>
    </form>
  );
};
